<script lang="ts">
import { defineComponent, PropType, computed } from 'vue'
import PublisherName from '@/components/contents/PublisherName.vue'

const DEFAULT_HEADING_OPTIONS = {
  TAG_NAME: 'p',
  LINE_HEIGHT: 1.4,
  FONT_SIZE: {
    PC: '0.875em',  // as 14px
    SP: '0.875em',
  },
  FONT_WEIGHT: 'normal',
  MAX_LINE: 0,
} as const

export default defineComponent({
  name: 'CommentSource',

  components: {
    PublisherName,
  },

  props: {
    content: {
      type: Object as PropType<ContentItemBase>,
      required: true,
    },

    hasLink: {
      type: Boolean,
      default: true,
    },

    headingOptions: {
      type: Object as PropType<{
        // HTML 要素名
        tagName?: keyof HTMLElementTagNameMap,

        // CSS の line-height 値
        lineHeight?: number,

        // CSS の font-size
        fontSize?: {
          pc: string,
          sp: string,
        },

        // 文字の太さ
        fontWeight?: 'normal' | 'bold',

        // 表示最大行数
        maxLine?: number,
      }>,
      default: () => ({
        tagName: DEFAULT_HEADING_OPTIONS.TAG_NAME,
        lineHeight: DEFAULT_HEADING_OPTIONS.LINE_HEIGHT,
        fontSize: {
          pc: DEFAULT_HEADING_OPTIONS.FONT_SIZE.PC,
          sp: DEFAULT_HEADING_OPTIONS.FONT_SIZE.SP,
        },
        fontWeight: DEFAULT_HEADING_OPTIONS.FONT_WEIGHT,
        maxLine: DEFAULT_HEADING_OPTIONS.MAX_LINE,
      })
    }
  },

  setup(props) {
    const wrapperTagName = computed<string>(() => {
      return !!props.content.to && props.hasLink
        ? 'a'
        : 'div'
    })

    const href = computed<string | undefined>(() => {
      return !!props.content.to && props.hasLink
        ? props.content.to
        : undefined
    })

    /**
     * 見出し部分の html とスタイルに関連する値
     */
    const computedHeadingOptions = computed<{
      tagName: keyof HTMLElementTagNameMap,
      maxHeight: string,
      lineHeight: number,
      fontSize: {
        pc: string,
        sp: string,
      },
      fontWeight: 'normal' | 'bold',
    }>(() => {
      // デフォルト値をベースに分割代入で props で渡された値をオーバーライド
      const _options = {
        ...{
          tagName: DEFAULT_HEADING_OPTIONS.TAG_NAME,
          lineHeight: DEFAULT_HEADING_OPTIONS.LINE_HEIGHT,
          fontSize: {
            pc: DEFAULT_HEADING_OPTIONS.FONT_SIZE.PC,
            sp: DEFAULT_HEADING_OPTIONS.FONT_SIZE.SP,
          },
          fontWeight: DEFAULT_HEADING_OPTIONS.FONT_WEIGHT,
          maxLine: DEFAULT_HEADING_OPTIONS.MAX_LINE,
        },
        ...props.headingOptions,
      }

      const _maxLine = _options.maxLine > 0
        ? _options.maxLine
        : DEFAULT_HEADING_OPTIONS.MAX_LINE
      const _lineHeight = _options.lineHeight > 0
        ? _options.lineHeight
        : DEFAULT_HEADING_OPTIONS.LINE_HEIGHT
      const _maxHeight = _maxLine * _lineHeight
      const _fontSize = {
        pc: _options.fontSize.pc || DEFAULT_HEADING_OPTIONS.FONT_SIZE.PC,
        sp: _options.fontSize.sp || DEFAULT_HEADING_OPTIONS.FONT_SIZE.SP,
      }
      const _fontWeight = _options.fontWeight || 'normal'

      return {
        tagName: _options.tagName || DEFAULT_HEADING_OPTIONS.TAG_NAME,
        maxHeight: _maxHeight ? `${_maxHeight}em` : 'none',
        lineHeight: _lineHeight,
        fontSize: _fontSize,
        fontWeight: _fontWeight,
      }
    })

    return {
      // computed
      wrapperTagName,
      href,
      computedHeadingOptions,
    }
  }
})
</script>

<template>
  <component
    :is="wrapperTagName"
    class="l-unit__commentSource"
    :href="href">
    <div class="l-unit__commentSource--header">
      <div class="o-heading__commentSource">
        <component
          :is="computedHeadingOptions.tagName"
          class="o-heading__commentSource--item"
          :style="{
            'max-height': computedHeadingOptions.maxHeight,
            'line-height': computedHeadingOptions.lineHeight,
            'font-weight': computedHeadingOptions.fontWeight,
            '--font-size-pc': computedHeadingOptions.fontSize.pc,
            '--font-size-sp': computedHeadingOptions.fontSize.sp,
          }">
          {{ content.title }}
        </component>
      <!-- /.o-heading__commentSource --></div>
    <!-- /.l-unit__commentSource header --></div>

    <div
      v-if="content.thumbImageUrl"
      class="l-unit__commentSource--image">
      <img
        class="img"
        :src="content.thumbImageUrl"
        loading="lazy"
        alt=""
        data-is-liquid="true">
    <!-- /.l-unit__commentSource image --></div>

    <div
      v-if="content.publisher?.name"
      class="l-unit__commentSource--footer is-publisher">
      <PublisherName
        :image="content.publisher.iconUrl"
        :label="content.publisher.name" />
    <!-- /.l-unit__commentSource footer --></div>
  <!-- /.l-unit__commentSource --></component>

</template>

<style lang="sass" scoped>
.l-unit__commentSource
  $root: &
  +base-layout(left, $root, 40, 40, 8, 8)
  color: $font-black
  text-decoration: none
  background-color: rgba($bg-light-gray, 1)
  +transition

  +mouseover
    #{$root}--header
      text-decoration: underline

    #{$root}--image
      opacity: 0.56

  & &--image
    +transition

  & &--footer
    &.is-publisher
      margin-top: 0.25em

// object
.o-heading__commentSource
  $root: &
  --font-size-pc: $font-large
  --font-size-sp: $font-large

  & &--item
    overflow: hidden
    color: $font-black
    font-size: var(--font-size-pc)

  +breakpoint
    #{$root}--item
      font-size: var(--font-size-sp)
</style>
